<script lang="ts" setup>
import appCan from '@@/bits/app_can'
import { ContentPickerSource } from '@@/bits/content_picker'
import { dir } from '@@/bits/current_dir'
import device from '@@/bits/device'
import { recordWhitelistedClick } from '@@/bits/pepin'
import { defineAsyncComponent } from '@@/bits/vue'
import { useContentPickerStore } from '@@/pinia/content_picker'
import { useDarkModeStore } from '@@/pinia/dark_mode'
import { usePostComposerModalStore } from '@@/pinia/post_composer_modal_store'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfaceContentPickerStore } from '@@/pinia/surface_content_picker'
import { useSurfaceDraftsStore } from '@@/pinia/surface_drafts'
import { useSurfacePermissionsStore } from '@@/pinia/surface_permissions'
import { useSurfacePostPropertiesStore } from '@@/pinia/surface_post_properties'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import { useSurfaceRequireNoticeStore } from '@@/pinia/surface_require_notice_store'
import { useSurfaceSectionsStore } from '@@/pinia/surface_sections'
import { useSurfaceWishArrangementStore } from '@@/pinia/surface_wish_arrangement'
import { useWindowSizeStore } from '@@/pinia/window_size'
import type { AttachmentProps, Post, WallGroupPost } from '@@/types'
import Teleport from '@@/vuecomponents/Teleport.vue'
import { usePostContentPickerSources } from '@@/vuecomposables/post_content_picker_sources'
import { storeToRefs } from 'pinia'
import { computed } from 'vue'

const ContentPicker = defineAsyncComponent(() => import('@@/vuecomponents/ContentPicker.vue'))
const SurfaceAddPostButton = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceAddPostButton.vue'))
const SurfaceGroupPicker = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceGroupPicker.vue'))

const { contentSources } = usePostContentPickerSources()

const postComposerModalStore = usePostComposerModalStore()
const contentPickerStore = useContentPickerStore()
const darkModeStore = useDarkModeStore()
const surfaceStore = useSurfaceStore()
const surfaceContentPickerStore = useSurfaceContentPickerStore()
const surfaceDraftsStore = useSurfaceDraftsStore()
const surfacePermissionsStore = useSurfacePermissionsStore()
const surfacePostsStore = useSurfacePostsStore()
const surfacePostPropertiesStore = useSurfacePostPropertiesStore()
const surfaceRequireNoticeStore = useSurfaceRequireNoticeStore()
const surfaceSectionsStore = useSurfaceSectionsStore()
const surfaceWishArrangementStore = useSurfaceWishArrangementStore()
const windowSizeStore = useWindowSizeStore()

const { showOverFileSizeModal } = surfaceStore
const { uploadFileActiveDraft, updateActiveDraft } = surfaceDraftsStore
const { hidePostContentPicker } = surfaceContentPickerStore
const { isDarkMode } = storeToRefs(darkModeStore)
const { windowHeight, isSmallerThanTabletPortrait } = storeToRefs(windowSizeStore)

/**
 * Opens the wall in a new window for LTI (Learning Tools Interoperability) handling.
 * This is used when a user needs to authenticate or access the wall through an LTI integration.
 * The URL opened is the login and redirect URL stored in the surface store.
 */
const openInNewWindow = (): void => {
  window.open(surfaceStore.loginAndRedirectToWallUrl, '_blank')
}

const onUploadStart = ({ url }: { url?: string }) => {
  if (url) {
    surfacePostsStore.saveContent({ content: { link: url } })
  }
}

const onUploadDone = (url: string, attachmentProps?: AttachmentProps) => {
  contentPickerStore.hideContentPickerPanel()
  const shouldCreateNewPost = ![ContentPickerSource.GoogleDrive, ContentPickerSource.MicrosoftOneDrive].includes(
    attachmentProps?.contentSource,
  )
  surfacePostsStore.saveContent({
    content: { link: url, altText: attachmentProps?.altText, shouldCreateNewPost },
  })
  if (surfaceDraftsStore.activeDraft?.shouldAutoPublish) {
    surfacePostsStore.savePost({ attributes: surfaceDraftsStore.activeDraft })
  }
}

const onUploadReject = (file: File) => {
  showOverFileSizeModal({ file })
}

const onUploadError = () => {
  surfaceDraftsStore.stopUploadFileActiveDraft()
  if (surfaceDraftsStore.activeDraft) {
    surfaceDraftsStore.stopAutoPublishDraft(surfaceDraftsStore.activeDraft.cid)
  }
}

const onUploadProgress = (progress: number) => {
  surfaceDraftsStore.updateActiveDraft({ uploadProgress: progress })
}

const showExtensionRequired = (): void => {
  surfaceRequireNoticeStore.showExtensionRequired()
  surfaceRequireNoticeStore.addAfterHideExtensionRequiredAction(() => {
    contentPickerStore.hideContentPickerPanel()
    hidePostContentPicker()
  })
}

const setCaption = (caption: string) => {
  if (!caption) return

  surfacePostsStore.saveAttachmentCaption({ caption })
}

const canShowAddPost = computed(() => {
  const isAppWithNewPosting = device.app && appCan('newPostModal')
  const isActiveDraftExpanded = postComposerModalStore.hasExpandedComposerModal && device.desktop && !device.phone
  const isAPostBeingEdited = !!surfacePostsStore.postBeingEditedCid
  return (
    !isAppWithNewPosting &&
    (surfaceStore.isEmbedded || !surfaceStore.isMobileLayout) &&
    surfacePermissionsStore.canIWrite &&
    !surfaceStore.isReadOnly &&
    (!isAPostBeingEdited || !isActiveDraftExpanded) &&
    surfaceDraftsStore.hasFetchedInitialPostDrafts &&
    // Reason why we can only show the add post button after dockable modal list is mounted:
    // `DockableModalList` has a prop called `initialModalState`. In a normal case, it is set
    // to `Minimized`. This is so that after we fetch post drafts, they appear as minimized.
    // If we show the add post button before the dockable modal list is mounted, and the user
    // clicks on the button to create a new draft, this draft will be considered as a draft
    // we fetch from the API and will appear minimized. This is not the desired behavior.
    // So if we delay showing this button until dockable modal list is mounted, we can avoid
    // the issue as users can only create drafts after dockable modal list is already rendered.
    // However, it is not perfect because we sacrifice Time to Interactive for this.
    // TODO: Improve this fix
    postComposerModalStore.isDockableModalListMounted
  )
})

const xAddPostButton = computed(() => {
  return canShowAddPost.value && !surfaceStore.isSubmissionRequest
})

const xContentPicker = computed(() => {
  return (
    (contentPickerStore.xContentSourceMenu === true || contentPickerStore.activeContentPickerPanel !== null) &&
    surfaceContentPickerStore.xPostContentPicker
  )
})

// #region Group picker
// TODO: this region only supports showing group picker for sections and not for other group types yet
const xSectionPicker = computed(() => {
  if (surfaceStore.canUseSections && surfaceStore.isSectioned) {
    const pendingPostsWithoutSections = surfacePostsStore.pendingPosts.filter((p: Post) => !p.wall_section_id)
    return pendingPostsWithoutSections.length > 0
  }
  return false
})

const handlePickingGroup = async (groupId?: WallGroupPost['id'], title?: string) => {
  if (title) {
    // Create new section with the provided title
    const section = await surfaceSectionsStore.createSectionWithName(title)
    if (section == null) return
    surfacePostsStore.pickSection({ sectionId: section.id })
  } else {
    if (groupId == null) return
    surfacePostsStore.pickSection({ sectionId: groupId as number })
  }
}

const handleCancelPickingGroup = () => {
  surfacePostsStore.cancelPickingSection()
}
// #endregion

const xSearchBar = computed(() => {
  return contentSources.value
    .flat()
    .filter((source) => source.visible)
    .some((source) =>
      [
        ContentPickerSource.ImageSearch,
        ContentPickerSource.GifSearch,
        ContentPickerSource.VideoSearch,
        ContentPickerSource.AudioSearch,
        ContentPickerSource.WebBrowser,
        ContentPickerSource.LinkInput,
        ContentPickerSource.AiImageSearch,
      ].includes(source.type as ContentPickerSource),
    )
})

const googleDriveUploadKey = computed(() => {
  return surfaceDraftsStore.activeDraft ? `draft:${surfaceDraftsStore.activeDraft.cid}` : undefined
})
</script>

<template>
  <div :dir="dir()" class="z-addpost" @click.stop="recordWhitelistedClick" @keydown.stop="() => {}">
    <Teleport v-if="xContentPicker" to="#content-picker-portal-target">
      <ContentPicker
        :enable-hidden-inputs="device.mobile"
        :preferred-content-source-menu-start-height="500"
        :content-source-menu-list="contentSources"
        :x-search-bar="xSearchBar"
        :is-online="surfaceStore.isOnline"
        :is-lti-embedded="surfaceStore.isLtiEmbedded"
        :should-upload-file="!surfaceDraftsStore.isAnyDraftActive"
        :active-draft="surfaceDraftsStore.activeDraft"
        :search-modes="surfaceContentPickerStore.currentSearchModes"
        :default-search-mode="surfaceContentPickerStore.defaultSearchMode"
        :search-suggestions="surfaceContentPickerStore.searchSuggestions"
        :google-drive-upload-key="googleDriveUploadKey"
        :user="surfaceStore.user"
        :wall="surfaceStore.wallAttributes"
        :wall-post-properties="surfacePostPropertiesStore.wallPostProperties"
        :teleport-target="'#surface-container'"
        @open-in-new-window="openInNewWindow"
        @close-content-picker="hidePostContentPicker"
        @upload-start="onUploadStart"
        @upload-done="onUploadDone"
        @upload-reject="onUploadReject"
        @upload-error="onUploadError"
        @upload-progress="onUploadProgress"
        @select-link="onUploadDone"
        @update-active-draft="updateActiveDraft"
        @select-file="uploadFileActiveDraft"
        @show-extension-required="showExtensionRequired"
        @set-caption="setCaption"
      />
    </Teleport>
    <SurfaceAddPostButton v-if="xAddPostButton" />
    <SurfaceGroupPicker
      v-if="xSectionPicker"
      :groups="surfaceWishArrangementStore.sortedGroups"
      :dark-mode="isDarkMode"
      :window-height="windowHeight"
      :full-size="isSmallerThanTabletPortrait"
      @pick-group="handlePickingGroup"
      @cancel-picking-group="handleCancelPickingGroup"
    />
  </div>
</template>
